// V2 Tempo — Weekly Modifier. A deterministic-per-week reward boost
// that gives the player a reason to "check what's different this week"
// even after they've exhausted the daily challenges loop.
//
// All modifiers are CLIENT-SIDE reward multipliers — they don't change
// sim mechanics (no balance risk, no test fallout, no doctrine modal
// retiming). The week-of-year picker is deterministic, so every player
// on the same week sees the same modifier; no server coordination
// required.
//
// Why a week-of-year picker:
//   - Daily challenges already rotate every 24h — adding a second daily
//     would water both down.
//   - A weekly modifier creates a clean "this week" identity ("Double XP
//     week" / "Mission XP Surge") that feels event-like.
//   - Week boundaries align with how people think about returning
//     ("I'll try again Saturday").

import { getTodayUtcDateStr } from './DailyChallenges.js';

export interface WeeklyModifier {
  id: 'double_xp' | 'mission_surge' | 'medal_boost' | 'daily_double' | 'streak_shield';
  name: string;
  description: string;
  // Pure-multiplier applied to match XP. 1.0 = no change.
  matchXpMultiplier: number;
  // Pure-multiplier applied to MissionBanner XP. 1.0 = no change.
  missionXpMultiplier: number;
  // If true, upgrade every Bronze medal earned to Silver this week (and
  // Silver to Gold) — purely cosmetic in the EOM card; doesn't change
  // sim.
  medalBoost: boolean;
  // If true, daily-challenge XP rewards count double (when we add per-
  // daily XP — currently unused but reserved).
  dailyDouble: boolean;
  // If true, a missed-day login this week doesn't break the streak
  // (single grace day; checked on next session visit).
  streakShield: boolean;
  // Tier color for the menu chip.
  color: string;
  glow: string;
}

const MODIFIERS: readonly WeeklyModifier[] = [
  {
    id: 'double_xp',
    name: 'Double XP',
    description: 'All match XP rewards are doubled this week.',
    matchXpMultiplier: 2.0,
    missionXpMultiplier: 1.0,
    medalBoost: false,
    dailyDouble: false,
    streakShield: false,
    color: '#6fdcff',
    glow: 'rgba(110,220,255,0.55)',
  },
  {
    id: 'mission_surge',
    name: 'Mission Surge',
    description: 'Mission rewards are tripled this week.',
    matchXpMultiplier: 1.0,
    missionXpMultiplier: 3.0,
    medalBoost: false,
    dailyDouble: false,
    streakShield: false,
    color: '#ffe680',
    glow: 'rgba(255,230,128,0.50)',
  },
  {
    id: 'medal_boost',
    name: 'Medal Boost',
    description: 'Bronze becomes Silver, Silver becomes Gold this week.',
    matchXpMultiplier: 1.0,
    missionXpMultiplier: 1.0,
    medalBoost: true,
    dailyDouble: false,
    streakShield: false,
    color: '#c66bff',
    glow: 'rgba(198,107,255,0.55)',
  },
  {
    id: 'daily_double',
    name: 'Daily Double',
    description: 'Daily challenges reward double bonus this week.',
    matchXpMultiplier: 1.0,
    missionXpMultiplier: 1.0,
    medalBoost: false,
    dailyDouble: true,
    streakShield: false,
    color: '#ff7a5a',
    glow: 'rgba(255,122,90,0.55)',
  },
  {
    id: 'streak_shield',
    name: 'Streak Shield',
    description: 'Missing one day this week will not break your login streak.',
    matchXpMultiplier: 1.0,
    missionXpMultiplier: 1.0,
    medalBoost: false,
    dailyDouble: false,
    streakShield: true,
    color: '#6ed788',
    glow: 'rgba(110,215,136,0.55)',
  },
];

// Week index since the Unix epoch — stable, deterministic, identical
// for every device in the same TZ-adjusted week. We use the player's
// LOCAL date for the picker so the rollover feels natural for them
// (Monday morning local rather than Monday morning UTC).
function weekIndexFromDateStr(dateStr: string): number {
  const t = Date.parse(`${dateStr}T00:00:00Z`);
  if (!Number.isFinite(t)) return 0;
  // Anchor at 2024-01-01 (a Monday); divide by ms-per-week.
  const anchor = Date.parse('2024-01-01T00:00:00Z');
  const weeks = Math.floor((t - anchor) / (7 * 86_400_000));
  return Math.max(0, weeks);
}

export function getThisWeekModifier(): WeeklyModifier {
  const today = getTodayUtcDateStr();
  const idx = weekIndexFromDateStr(today);
  return MODIFIERS[idx % MODIFIERS.length]!;
}

// Convenience: scale a raw XP delta by the active modifier. Always
// rounds DOWN to keep totals deterministic.
export function applyMatchXpModifier(rawDelta: number): number {
  return Math.floor(rawDelta * getThisWeekModifier().matchXpMultiplier);
}

// Convenience: scale a raw mission XP reward by the active modifier.
export function applyMissionXpModifier(rawDelta: number): number {
  return Math.floor(rawDelta * getThisWeekModifier().missionXpMultiplier);
}
